// "My avatar": the avatar creator. Body and face sliders, skin, eyes, hair and an outfit from the cosmetics you own,
// saved to the accounts profile `avatar`. Everything offered comes from the store's wearables listing in money
// (`GET /v1/store/wearables`); the controls are built from each package's params, so new bodies and garments show
// up without changes here. The preview is the real avatar SDK (sdk/avatar, `thrdm.avatar.load`) on three.js,
// loaded only on this page; if it can't load, the page still works.

import { api, ApiError, isMissingEndpoint } from "../api.ts";
import { clear, errorBox, h, link, toast } from "../dom.ts";
import { parseMoney, priceLabel, showMoney, viewerCurrency, type Currency, type Prices } from "../money.ts";
import { selling } from "../selling.ts";
import { setTitle } from "../router.ts";
import { loadMe, type Me } from "../session.ts";
import { startAvatarPreview } from "../preview-frame.ts";
import type { AvatarClip } from "../frame/protocol.ts";

export const PACKAGES = "/store/packages/";
export const HUMAN = "threedom/avatar-human";

type Value = number | string;
export interface AvatarPart {
  id: string;
  params?: Record<string, Value>;
}
export interface AvatarSpec {
  body: AvatarPart;
  outfit: AvatarPart[];
}

/** One entry of money's public listing (`GET /v1/store/wearables`). */
export interface Wearable {
  id: string; // money's item id, a_<path>
  path: string; // store package, what profiles store
  title: string;
  kind: string;
  slot: string; // "body" for avatar bodies, else the wear slot
  /** US cents in the starter list (money's listing sends `prices` instead, wave 10) */
  price?: number | { amount_minor: number; currency: string };
  /** money's listing: `{USD: Money, EUR: Money}` (wave 10) */
  prices?: Prices;
  free: boolean;
  display?: string;
  preview: string | null;
}

/** True for a paid wearable, whichever price shape money sent. */
export function wearablePaid(w: Pick<Wearable, "price" | "free"> & { prices?: Prices }): boolean {
  if (w.free) return false;
  if (typeof w.price === "number") return w.price > 0;
  const m = parseMoney(w.price) ?? parseMoney(w.prices?.USD) ?? parseMoney(w.prices?.EUR);
  return (m?.amount_minor ?? 0) > 0;
}

/** A wearable's price in the viewer's currency (`$5.00`), or "Free". */
export function wearablePrice(w: Pick<Wearable, "price" | "prices" | "free">, currency: Currency = viewerCurrency()): string {
  if (!wearablePaid(w)) return "Free";
  if (typeof w.price === "number") return priceLabel(w.price, currency, w.prices);
  const own = parseMoney(w.prices?.[currency]);
  return showMoney(own ?? w.price ?? w.prices?.USD ?? w.prices?.EUR);
}

interface ParamDef {
  type: string;
  minimum?: number;
  maximum?: number;
  default?: Value;
  enum?: string[];
  format?: string;
  description?: string;
}
interface Manifest {
  id: string;
  title: string;
  kind: string;
  price?: number;
  wear?: { slot: string };
  params?: { properties?: Record<string, ParamDef> };
}

/** What an unreadable saved avatar falls back to: the realistic body with nothing worn. */
export const DEFAULT_AVATAR: AvatarSpec = { body: { id: HUMAN, params: { height: 1.74, gender: 0.5, skin: "#c99a7c" } }, outfit: [] };

/** Where someone without a saved avatar starts: dressed, with hair. */
export const NEW_LOOK: AvatarSpec = {
  body: DEFAULT_AVATAR.body,
  outfit: [{ id: "threedom/avatar-hair", params: { style: "short02", colour: "#3b2618" } }, { id: "threedom/avatar-tshirt" }, { id: "threedom/avatar-jeans" }, { id: "threedom/avatar-sneakers" }],
};

/** Starting looks: a quick way in before the sliders. */
export const STARTERS: { name: string; spec: AvatarSpec }[] = [
  { name: "Ana", spec: { body: { id: HUMAN, params: { height: 1.66, gender: 0.08, build: 0.95, skin: "#e0b49a", eyes: "green", brows: "#6b4a2a", lips: 0.3 } }, outfit: [{ id: "threedom/avatar-hair", params: { style: "bob02", colour: "#6b4a2a" } }, { id: "threedom/avatar-tshirt", params: { colour: "#2f6f5e" } }, { id: "threedom/avatar-jeans" }, { id: "threedom/avatar-sneakers" }] } },
  { name: "Kofi", spec: { body: { id: HUMAN, params: { height: 1.83, gender: 0.95, build: 1.05, muscle: 0.65, skin: "#6b4430", brows: "#140e0b" } }, outfit: [{ id: "threedom/avatar-hair", params: { style: "short04", colour: "#140e0b" } }, { id: "threedom/avatar-tshirt", params: { colour: "#f2f2f0" } }, { id: "threedom/avatar-jeans", params: { colour: "#2b2b2e" } }, { id: "threedom/avatar-sneakers" }] } },
  { name: "Mei", spec: { body: { id: HUMAN, params: { height: 1.6, gender: 0.05, build: 0.9, skin: "#e8c4a6", brows: "#1a1412", eyes: "brown" } }, outfit: [{ id: "threedom/avatar-hair", params: { style: "long01", colour: "#1a1412" } }, { id: "threedom/avatar-tshirt", params: { colour: "#c8402e" } }, { id: "threedom/avatar-jeans" }, { id: "threedom/avatar-sneakers" }] } },
  { name: "Sam", spec: { body: { id: HUMAN, params: { height: 1.76, gender: 0.5, build: 1.15, skin: "#b07a58", brows: "#3b2618" } }, outfit: [{ id: "threedom/avatar-hair", params: { style: "afro01", colour: "#2a1a10" } }, { id: "threedom/avatar-tshirt", params: { colour: "#e0b040" } }, { id: "threedom/avatar-jeans" }] } },
];

/** Store package id → the `a_` id money uses for ownership. */
export function assetId(pkg: string): string {
  return `a_${pkg}`;
}

/** Used when money has no wearables listing (older money builds): the store's own packages. */
export const FALLBACK_WEARABLES: Wearable[] = (
  [
    ["threedom/avatar-human", "Human (realistic)", "character", "body", 0],
    ["threedom/mannequin", "Mannequin (humanoid rig)", "character", "body", 0],
    ["threedom/avatar-hair", "Hair", "clothing", "hair", 0],
    ["threedom/avatar-tshirt", "T-shirt", "clothing", "top", 0],
    ["threedom/avatar-jeans", "Jeans", "clothing", "bottom", 0],
    ["threedom/avatar-sneakers", "Sneakers", "clothing", "shoes", 0],
    ["threedom/cap", "Baseball Cap", "clothing", "hat", 500],
    ["threedom/backpack", "Small Backpack", "clothing", "back", 0],
  ] as [string, string, string, string, number][]
).map(([path, title, kind, slot, price]) => ({
  id: assetId(path), path, title, kind, slot, price, free: price === 0, preview: `${path}/preview.png`,
}));

/** The wearables to offer: all while selling; otherwise only free or owned ones (no prices, nothing to buy). */
export function wearableChoices(list: Wearable[], owns: (w: Wearable) => boolean, isOn: boolean): Wearable[] {
  return isOn ? list : list.filter(owns);
}

// ---- spec helpers (pure; tested in test/logic.test.ts) -----------------------------------------------------

/** Reads the saved avatar: the structured `avatar` field, else JSON in `avatarRef`, else the default. */
export function avatarFromMe(me: Pick<Me, "avatar" | "avatarRef">): AvatarSpec {
  const ok = (a: unknown): a is AvatarSpec =>
    !!a && typeof a === "object" && typeof (a as AvatarSpec).body?.id === "string" && Array.isArray((a as AvatarSpec).outfit);
  if (ok(me.avatar)) return structuredClone(me.avatar);
  if (me.avatarRef) {
    try {
      const a = JSON.parse(me.avatarRef);
      if (ok(a)) return a;
    } catch {
      /* an opaque ref from somewhere else */
    }
  }
  return structuredClone(DEFAULT_AVATAR);
}

/** One item per slot: wearing something replaces what's in its slot. Keeps the item's params when it stays on. */
export function wear(spec: AvatarSpec, item: { id: string; slot: string; params?: Record<string, Value> }, on: boolean, slotOf: (id: string) => string | undefined): AvatarSpec {
  const kept = spec.outfit.find((o) => o.id === item.id);
  const outfit = spec.outfit.filter((o) => o.id !== item.id && (!on || slotOf(o.id) !== item.slot));
  if (on) outfit.push(item.params ? { id: item.id, params: item.params } : (kept ?? { id: item.id }));
  return { ...spec, outfit };
}

/** Set one param on the body (`id` = the body's id) or on a worn item. */
export function setParam(spec: AvatarSpec, id: string, key: string, value: Value): AvatarSpec {
  const put = (p: AvatarPart): AvatarPart => ({ ...p, params: { ...p.params, [key]: value } });
  if (spec.body.id === id) return { ...spec, body: put(spec.body) };
  return { ...spec, outfit: spec.outfit.map((o) => (o.id === id ? put(o) : o)) };
}

/** Drop params equal to the package default, so saved profiles stay small. */
export function compact(spec: AvatarSpec, defaults: (id: string) => Record<string, Value | undefined>): AvatarSpec {
  const trim = (p: AvatarPart): AvatarPart => {
    const d = defaults(p.id);
    const params = Object.fromEntries(Object.entries(p.params ?? {}).filter(([k, v]) => d[k] !== v));
    return Object.keys(params).length ? { id: p.id, params } : { id: p.id };
  };
  return { body: trim(spec.body), outfit: spec.outfit.map(trim) };
}

/** Never show (or save) items the player doesn't own, or ones the listing doesn't know. */
export function ownedOnly(spec: AvatarSpec, wearables: Wearable[], owns: (w: Wearable) => boolean): AvatarSpec {
  return {
    ...spec,
    outfit: spec.outfit.filter((o) => {
      const w = wearables.find((x) => x.path === o.id);
      return !!w && owns(w);
    }),
  };
}

/** How each known body param is shown; unknown numeric params still get a plain slider. */
export const LABELS: Record<string, { label: string; group: "body" | "face"; ends?: [string, string] }> = {
  gender: { label: "Body shape", group: "body", ends: ["Feminine", "Masculine"] },
  height: { label: "Height", group: "body" },
  build: { label: "Build", group: "body", ends: ["Slim", "Heavy"] },
  muscle: { label: "Muscle", group: "body", ends: ["Soft", "Toned"] },
  proportions: { label: "Proportions", group: "body", ends: ["Everyday", "Idealised"] },
  shoulders: { label: "Shoulders", group: "body", ends: ["Narrow", "Broad"] },
  hips: { label: "Hips", group: "body", ends: ["Narrow", "Wide"] },
  legs: { label: "Legs", group: "body", ends: ["Shorter", "Longer"] },
  bust: { label: "Bust", group: "body", ends: ["Smaller", "Fuller"] },
  faceWidth: { label: "Face", group: "face", ends: ["Lean", "Full"] },
  jaw: { label: "Jaw", group: "face", ends: ["Narrow", "Wide"] },
  chin: { label: "Chin", group: "face", ends: ["Receding", "Prominent"] },
  cheekbones: { label: "Cheekbones", group: "face", ends: ["Soft", "High"] },
  eyeSize: { label: "Eyes", group: "face", ends: ["Smaller", "Larger"] },
  noseWidth: { label: "Nose width", group: "face", ends: ["Narrow", "Wide"] },
  noseLength: { label: "Nose length", group: "face", ends: ["Short", "Long"] },
  lips: { label: "Lips", group: "face", ends: ["Thin", "Full"] },
};
const HIDDEN_PARAMS = new Set(["lod"]);

export const SKIN_TONES = ["#f3d4c0", "#e8c4a6", "#d9a88a", "#c99a7c", "#b07a58", "#8d5a3b", "#6b4430", "#4a2e20"];
export const HAIR_COLOURS = ["#140e0b", "#2a1a10", "#3b2618", "#6b4a2a", "#9a6a3a", "#c9a060", "#e2cfa0", "#8a8a8a", "#a8402a"];
export const CLOTH_COLOURS = ["#f2f2f0", "#1c1c20", "#2d4f7a", "#3d5a80", "#2f6f5e", "#c8402e", "#e0b040", "#7a4aa0", "#8a8a8a"];
export const HAIR_STYLES: Record<string, string> = { short02: "Short", short04: "Cropped", bob02: "Bob", ponytail01: "Ponytail", braid01: "Braid", long01: "Long", afro01: "Curls" };
const SLOT_NAMES: Record<string, string> = { top: "Top", bottom: "Bottom", shoes: "Shoes", hat: "Hat", back: "Back", face: "Face", hands: "Hands", accessory: "Accessory" };
const COLOUR_LABEL: Record<string, string> = { skin: "Skin tone", colour: "Skin tone", brows: "Eyebrows", underwear: "Base layer" };

// ---- saving and loading ----------------------------------------------------------------------------------------

/**
 * Save to accounts. Prefers the structured `avatar` field; older accounts builds reject unknown fields, so then it
 * falls back to a compact JSON `avatarRef` (≤ 200 chars).
 */
export async function saveAvatar(spec: AvatarSpec): Promise<"avatar" | "avatarRef"> {
  try {
    await api("accounts", "/me", { method: "PATCH", body: { avatar: spec } });
    return "avatar";
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 400 && /avatar/i.test(e.message) && /unknown/i.test(e.message))) throw e;
  }
  const ref = JSON.stringify(spec);
  if (ref.length > 200) throw new Error("This avatar is too detailed to save until accounts stores avatars properly.");
  await api("accounts", "/me", { method: "PATCH", body: { avatarRef: ref } });
  return "avatarRef";
}

/**
 * The saved avatar's picture for profiles: captured from the preview, PUT to accounts. Best effort: an accounts build
 * without the endpoint, or a preview that can't render, leaves the profile on its initial.
 */
export async function savePortrait(preview: { portrait(): Promise<Blob | null> }): Promise<string | null> {
  try {
    const jpeg = await preview.portrait();
    if (!jpeg) return null;
    const r = await api<{ avatarPortrait: string }>("accounts", "/me/avatar/portrait", { method: "PUT", bytes: jpeg });
    return r.avatarPortrait;
  } catch (e) {
    if (isMissingEndpoint(e)) return null;
    console.warn("avatar portrait not saved", e);
    return null;
  }
}

/** The preview's motions: idle, walking on the spot, and the emotes people use in the Plaza. */
export const MOTIONS: [AvatarClip, string][] = [["idle", "Idle"], ["walk", "Walk"], ["wave", "Wave"], ["jump", "Jump"], ["cheer", "Cheer"], ["clap", "Clap"], ["dance", "Dance"]];

/** Money's public wearables listing; the starter list when money doesn't have one (or is down). */
async function listing(): Promise<{ items: Wearable[]; live: boolean }> {
  try {
    const r = await api<{ wearables: Wearable[] }>("money", "/store/wearables", { auth: false });
    if (r.wearables.length) return { items: r.wearables, live: true };
  } catch (e) {
    if (!isMissingEndpoint(e) && !(e instanceof ApiError && e.status >= 500) && e instanceof ApiError) throw e;
  }
  return { items: FALLBACK_WEARABLES, live: false };
}

async function manifest(path: string): Promise<Manifest | null> {
  try {
    const r = await fetch(`${PACKAGES}${path}/asset.json`);
    return r.ok ? ((await r.json()) as Manifest) : null;
  } catch {
    return null;
  }
}

/** Owned store cosmetics from money (`GET /v1/me/inventory`). Free items are always yours. */
async function owned(signedIn: boolean): Promise<{ ids: Set<string>; known: boolean }> {
  if (!signedIn) return { ids: new Set(), known: false };
  try {
    const r = await api<{ items: { item_id: string }[] }>("money", "/me/inventory?kind=cosmetic");
    return { ids: new Set(r.items.map((i) => i.item_id)), known: true };
  } catch (e) {
    if (isMissingEndpoint(e)) return { ids: new Set(), known: false };
    throw e;
  }
}

// ---- page ------------------------------------------------------------------------------------------------------

type Tab = "body" | "face" | "look" | "outfit";
const TABS: [Tab, string][] = [["body", "Body"], ["face", "Face"], ["look", "Skin & hair"], ["outfit", "Outfit"]];

export async function avatarPage(): Promise<HTMLElement> {
  setTitle("My avatar");
  const me = await loadMe();
  const [{ items: wearables, live }, own] = await Promise.all([listing(), owned(!!me)]);
  const manifests = new Map<string, Manifest>();
  await Promise.all(wearables.map(async (w) => { const m = await manifest(w.path); if (m) manifests.set(w.path, m); }));
  const bodies = wearables.filter((w) => w.slot === "body" && manifests.has(w.path));
  const owns = (w: Wearable) => !wearablePaid(w) || own.ids.has(w.id);
  // without selling (wave 9) wearables you'd have to buy leave the lists; what you own stays
  const clothes = wearableChoices(wearables.filter((w) => w.slot !== "body" && manifests.has(w.path)), owns, selling());
  const slotOf = (id: string) => clothes.find((w) => w.path === id)?.slot;
  const propsOf = (id: string) => manifests.get(id)?.params?.properties ?? {};
  const defaults = (id: string) => Object.fromEntries(Object.entries(propsOf(id)).map(([k, d]) => [k, d.default]));

  const saved = me && (me.avatar || me.avatarRef) ? avatarFromMe(me) : structuredClone(NEW_LOOK);
  const start = (s: AvatarSpec): AvatarSpec => {
    const next = ownedOnly(s, clothes, owns);
    return manifests.has(next.body.id) || !bodies[0] ? next : { ...next, body: { id: bodies[0].path, params: {} } };
  };
  let spec = start(saved);
  let tab: Tab = "body";
  let dirty = false;

  const stage = h("div", { class: "stage", "aria-label": "Avatar preview", role: "img" }, h("p", { class: "stage-note" }, "Loading preview…"));
  const preview = startAvatarPreview(stage);
  const status = h("div", { "aria-live": "polite" });
  let motion: AvatarClip = "idle";
  const motions = h("div", { class: "chips motions", role: "group", "aria-label": "Preview motion" });
  // walk toggles; a one-shot plays once and the buttons go back to idle
  const pickMotion = (c: AvatarClip) => {
    motion = c === "walk" && motion === "walk" ? "idle" : c;
    preview.play(motion);
    drawMotions();
    if (motion !== "walk" && motion !== "idle") setTimeout(() => { if (motion === c) { motion = "idle"; drawMotions(); } }, 2500);
  };
  const drawMotions = (): void =>
    clear(
      motions,
      MOTIONS.map(([c, label]) =>
        h("button", { type: "button", class: "chip", "aria-pressed": String(motion === c), onclick: () => pickMotion(c) }, label),
      ),
    );
  drawMotions();
  const controls = h("div", { class: "creator-controls" });
  const update = (next: AvatarSpec, redraw = false) => {
    spec = next;
    dirty = true;
    preview.show(spec);
    if (redraw) draw();
  };
  const bodyParam = (k: string): Value | undefined => spec.body.params?.[k] ?? propsOf(spec.body.id)[k]?.default;

  const slider = (id: string, key: string, def: ParamDef, value: Value | undefined) => {
    const meta = LABELS[key];
    const v = Number(value ?? def.default ?? 0);
    const min = def.minimum ?? 0, max = def.maximum ?? 1;
    const text = (n: number) => (key === "height" ? `${n.toFixed(2)} m` : "");
    const out = h("span", { class: "value" }, text(v));
    const input = h("input", {
      type: "range", min, max, step: key === "height" ? 0.01 : (max - min) / 100, value: String(v), "aria-label": meta?.label ?? key,
      oninput: (e: Event) => {
        const n = Number((e.target as HTMLInputElement).value);
        out.textContent = text(n);
        update(setParam(spec, id, key, Math.round(n * 1000) / 1000));
      },
    });
    return h(
      "label",
      { class: "slider" },
      h("span", { class: "slider-head" }, h("span", null, meta?.label ?? def.description ?? key), out),
      input,
      meta?.ends ? h("span", { class: "ends", "aria-hidden": "true" }, h("span", null, meta.ends[0]), h("span", null, meta.ends[1])) : null,
    );
  };

  const swatches = (label: string, colours: string[], current: string, pick: (c: string) => void) =>
    h(
      "fieldset",
      { class: "swatches" },
      h("legend", null, label),
      colours.map((c) =>
        h("button", { type: "button", class: "chip-colour", style: { background: c }, "aria-label": `${label} ${c}`, "aria-pressed": String(current.toLowerCase() === c), onclick: () => pick(c) }),
      ),
      h("input", { type: "color", class: "swatch", value: /^#[0-9a-f]{6}$/i.test(current) ? current : colours[0]!, "aria-label": `${label}: custom colour`, oninput: (e: Event) => pick((e.target as HTMLInputElement).value) }),
    );

  const chips = (label: string, options: [string, string][], current: string, pick: (v: string) => void) =>
    h(
      "fieldset",
      { class: "chips" },
      h("legend", null, label),
      options.map(([v, text]) => h("button", { type: "button", class: "chip", "aria-pressed": String(current === v), onclick: () => pick(v) }, text)),
    );

  const bodyPanel = (group: "body" | "face"): HTMLElement => {
    const props = propsOf(spec.body.id);
    const order = Object.keys(LABELS);
    const rank = (k: string) => (order.includes(k) ? order.indexOf(k) : order.length);
    const keys = Object.keys(props)
      .filter((k) => !HIDDEN_PARAMS.has(k) && (props[k]!.type === "number" || props[k]!.type === "integer") && (LABELS[k]?.group ?? "body") === group)
      .sort((a, b) => rank(a) - rank(b));
    return h(
      "div",
      null,
      group === "body" && bodies.length > 1 ? chips("Body", bodies.map((b) => [b.path, b.title]), spec.body.id, (id) => update({ ...spec, body: { id, params: {} } }, true)) : null,
      keys.length ? keys.map((k) => slider(spec.body.id, k, props[k]!, bodyParam(k))) : h("p", { class: "dim" }, group === "face" ? "This body has no face controls." : "This body has no shape controls."),
    );
  };

  const lookPanel = (): HTMLElement => {
    const props = propsOf(spec.body.id);
    const keys = Object.keys(props).filter((k) => !HIDDEN_PARAMS.has(k));
    const colourKeys = keys.filter((k) => props[k]!.type === "string" && props[k]!.format === "color");
    const enumKeys = keys.filter((k) => props[k]!.type === "string" && props[k]!.enum);
    const hairItems = clothes.filter((w) => w.slot === "hair" && owns(w));
    const worn = spec.outfit.find((o) => slotOf(o.id) === "hair");
    const hairProps = worn ? propsOf(worn.id) : {};
    const hairColour = String(worn?.params?.colour ?? hairProps.colour?.default ?? "");
    const hairOptions: [string, string][] = [["", "None"]];
    for (const w of hairItems) {
      const st = propsOf(w.path).style;
      if (st?.enum) for (const s of st.enum) hairOptions.push([`${w.path}#${s}`, HAIR_STYLES[s] ?? s]);
      else hairOptions.push([w.path, w.title]);
    }
    const hairNow = worn ? (hairProps.style ? `${worn.id}#${worn.params?.style ?? hairProps.style.default}` : worn.id) : "";
    return h(
      "div",
      null,
      colourKeys.map((k) =>
        swatches(COLOUR_LABEL[k] ?? k, k === "skin" || k === "colour" ? SKIN_TONES : k === "underwear" ? CLOTH_COLOURS : HAIR_COLOURS, String(bodyParam(k) ?? ""), (c) => update(setParam(spec, spec.body.id, k, c), true)),
      ),
      enumKeys.map((k) => chips(k === "eyes" ? "Eye colour" : k, props[k]!.enum!.map((v): [string, string] => [v, v[0]!.toUpperCase() + v.slice(1)]), String(bodyParam(k)), (v) => update(setParam(spec, spec.body.id, k, v), true))),
      hairItems.length
        ? chips("Hair", hairOptions, hairNow, (v) => {
            let next: AvatarSpec = { ...spec, outfit: spec.outfit.filter((o) => slotOf(o.id) !== "hair") };
            if (v) {
              const [path, style] = v.split("#") as [string, string | undefined];
              const params: Record<string, Value> = { ...(worn?.id === path ? worn.params : {}) };
              if (style) params.style = style;
              next = wear(next, { id: path, slot: "hair", params }, true, slotOf);
            }
            update(next, true);
          })
        : null,
      worn && hairProps.colour
        ? swatches("Hair colour", HAIR_COLOURS, hairColour, (c) => {
            let next = setParam(spec, worn.id, "colour", c);
            // eyebrows follow the hair unless they were set to something else
            if (props.brows && String(bodyParam("brows")).toLowerCase() === hairColour.toLowerCase()) next = setParam(next, spec.body.id, "brows", c);
            update(next, true);
          })
        : null,
    );
  };

  const outfitPanel = (): HTMLElement => {
    const slots = [...new Set(clothes.filter((w) => w.slot !== "hair").map((w) => w.slot))];
    return h(
      "div",
      null,
      !me ? h("p", { class: "dim small" }, "Sign in to wear items you've bought.") : own.known ? null : h("p", { class: "dim small" }, "Only free items for now: money doesn't list what you own yet."),
      slots.map((slot) => {
        const items = clothes.filter((w) => w.slot === slot);
        const worn = spec.outfit.find((o) => slotOf(o.id) === slot);
        const colour = worn ? propsOf(worn.id).colour : undefined;
        return h(
          "fieldset",
          { class: "slot" },
          h("legend", null, SLOT_NAMES[slot] ?? slot),
          h(
            "ul",
            { class: "options" },
            h("li", null, h("label", { class: "option" }, h("input", { type: "radio", name: `slot-${slot}`, checked: !worn, onchange: () => update({ ...spec, outfit: spec.outfit.filter((o) => slotOf(o.id) !== slot) }, true) }), h("span", { class: "grow" }, h("strong", null, "None")))),
            items.map((w) => {
              const mine = owns(w);
              return h(
                "li",
                null,
                h(
                  "label",
                  { class: `option${mine ? "" : " locked"}` },
                  h("input", { type: "radio", name: `slot-${slot}`, "aria-label": `Wear ${w.title}`, checked: worn?.id === w.path, disabled: !mine, onchange: () => update(wear(spec, { id: w.path, slot }, true, slotOf), true) }),
                  w.preview ? h("img", { src: `${PACKAGES}${w.preview}`, alt: "", loading: "lazy" }) : null,
                  h(
                    "span",
                    { class: "grow" },
                    h("strong", null, w.title),
                    h("span", { class: "dim small" }, wearablePaid(w) ? (mine ? "Owned" : wearablePrice(w)) : "Free"),
                    mine ? null : h("span", { class: "dim small" }, "Get it in the Banger app's store"),
                  ),
                ),
              );
            }),
          ),
          worn && colour ? swatches("Colour", CLOTH_COLOURS, String(worn.params?.colour ?? colour.default ?? ""), (c) => update(setParam(spec, worn.id, "colour", c), true)) : null,
        );
      }),
    );
  };

  const draw = () => {
    const panel = tab === "body" ? bodyPanel("body") : tab === "face" ? bodyPanel("face") : tab === "look" ? lookPanel() : outfitPanel();
    clear(
      controls,
      h(
        "div",
        { class: "starters", role: "group", "aria-label": "Start from a look" },
        h("span", { class: "dim small" }, "Start from"),
        STARTERS.filter((s) => manifests.has(s.spec.body.id)).map((s) => h("button", { type: "button", class: "chip", onclick: () => update(start(structuredClone(s.spec)), true) }, s.name)),
      ),
      h(
        "div",
        { class: "tabs-row creator-tabs", role: "tablist" },
        TABS.map(([t, label]) =>
          h("button", { type: "button", role: "tab", class: "tab", "aria-selected": String(tab === t), onclick: () => { tab = t; preview.focus(t === "face" || t === "look" ? "face" : "full"); draw(); } }, label),
        ),
      ),
      h("section", { class: "panel creator-panel", role: "tabpanel" }, panel),
      h(
        "div",
        { class: "actions" },
        me
          ? h(
              "button",
              {
                type: "button",
                class: "btn",
                onclick: async () => {
                  try {
                    const where = await saveAvatar(compact(spec, defaults));
                    dirty = false;
                    if (where === "avatar") await savePortrait(preview);
                    await loadMe(true);
                    clear(status, where === "avatarRef" ? h("p", { class: "dim small" }, "Saved (to avatarRef until accounts has the avatar field).") : null);
                    toast("Avatar saved. Games that support avatars will use it.");
                  } catch (e) {
                    clear(status, errorBox(e));
                  }
                },
              },
              "Save avatar",
            )
          : link("/signin?next=/me/avatar", { class: "btn" }, "Sign in to save"),
        h("button", { type: "button", class: "btn ghost", onclick: () => { spec = start(structuredClone(saved)); dirty = false; preview.show(spec); draw(); } }, "Undo changes"),
        h("button", { type: "button", class: "btn ghost", onclick: () => update(start(structuredClone(NEW_LOOK)), true) }, "Reset"),
      ),
      status,
      live ? null : h("p", { class: "dim small" }, "Showing the store's starter items: money's wearables listing isn't available."),
    );
  };
  draw();
  preview.show(spec);
  const guard = (e: BeforeUnloadEvent) => {
    if (!stage.isConnected) return window.removeEventListener("beforeunload", guard);
    if (dirty) e.preventDefault();
  };
  window.addEventListener("beforeunload", guard);

  return h(
    "div",
    null,
    h("h1", { style: { marginTop: "32px" } }, "My avatar"),
    h("p", { class: "dim" }, "Your look in Banger worlds and in games that use it. Games can restyle it to fit their art."),
    h("div", { class: "avatar-page" }, h("div", { class: "stage-wrap" }, stage, motions), controls),
  );
}
